/**
 * Employee Bank Account — admin edit.
 * GET is self-or-(VIEW/MANAGE); PUT is MANAGE only (bank-detail changes are a common fraud
 * vector and always go through someone accountable in HR — by design, not self-service).
 */
import { useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import { leaveApi } from "../../leave/api";
import type { EmployeeOption } from "../../leave/types";
import { payrollApi } from "../api";
import type { EmployeeBankAccountRequest } from "../types";
import { useAsyncData, useMutation } from "../hooks";
import {
  Btn, Card, CardHeader, PageHeader, PayrollError, PayrollLoading, SearchInput,
} from "../components/PayrollUI";

function empLabel(e: EmployeeOption) {
  return e.fullName || e.employeeName || e.name || e.employeeNumber;
}

const emptyForm: EmployeeBankAccountRequest = { bankName: "", branchName: "", accountName: "", accountNumber: "" };

export default function PayrollBankAccountPage() {
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canManage = isAdmin || hasPermission("HR_PAYROLL_MANAGE");

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<EmployeeOption | null>(null);
  const [form, setForm] = useState<EmployeeBankAccountRequest>(emptyForm);

  const employees = useAsyncData(() => leaveApi.employeeOptions(), []);
  const account = useAsyncData(
    () => (selected ? payrollApi.getBankAccount(selected.id) : Promise.resolve(null)),
    [selected?.id],
  );

  useEffect(() => {
    if (account.data) {
      setForm({
        bankName: account.data.bankName ?? "",
        branchName: account.data.branchName ?? "",
        accountName: account.data.accountName ?? "",
        accountNumber: account.data.accountNumber ?? "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [account.data]);

  const save = useMutation((payload: EmployeeBankAccountRequest) => payrollApi.updateBankAccount(selected!.id, payload));

  async function doSave() {
    await save.mutate(form);
    account.reload();
  }

  const filtered = (employees.data ?? []).filter((e) =>
    !search || empLabel(e).toLowerCase().includes(search.toLowerCase()) || e.employeeNumber.toLowerCase().includes(search.toLowerCase()),
  );

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance · Setup"
        title="Employee Bank Accounts"
      />

      <Card noPad>
        <CardHeader title="Select Employee" />
        <div className="p-5 space-y-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name or employee number…" />
          {employees.loading && <PayrollLoading />}
          {employees.error && <PayrollError message={employees.error} onRetry={employees.reload} />}
          {!employees.loading && !employees.error && (
            <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-100">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-slate-400">No employees match.</div>
              ) : filtered.slice(0, 50).map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelected(e)}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition hover:bg-slate-50 ${selected?.id === e.id ? "bg-indigo-50" : ""}`}
                >
                  <span className="font-medium text-slate-800">{empLabel(e)}</span>
                  <span className="font-mono text-xs text-slate-400">{e.employeeNumber}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {selected && (
        <Card noPad>
          <CardHeader title={`Bank Account — ${empLabel(selected)}`} subtitle={selected.employeeNumber} />
          {account.loading ? <PayrollLoading /> : account.error ? (
            <div className="p-5"><PayrollError message={account.error} onRetry={account.reload} /></div>
          ) : (
            <div className="p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-xs font-semibold text-slate-500">
                  Bank Name
                  <input className={`mt-1 ${inp}`} value={form.bankName ?? ""} onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} disabled={!canManage} />
                </label>
                <label className="block text-xs font-semibold text-slate-500">
                  Branch
                  <input className={`mt-1 ${inp}`} value={form.branchName ?? ""} onChange={(e) => setForm((f) => ({ ...f, branchName: e.target.value }))} disabled={!canManage} />
                </label>
                <label className="block text-xs font-semibold text-slate-500">
                  Account Name
                  <input className={`mt-1 ${inp}`} value={form.accountName ?? ""} onChange={(e) => setForm((f) => ({ ...f, accountName: e.target.value }))} disabled={!canManage} />
                </label>
                <label className="block text-xs font-semibold text-slate-500">
                  Account Number
                  <input className={`mt-1 ${inp}`} value={form.accountNumber ?? ""} onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))} disabled={!canManage} />
                </label>
              </div>
              {save.error && <p className="mt-3 text-sm text-red-600">{save.error}</p>}
              {canManage && (
                <div className="mt-4 flex justify-end">
                  <Btn variant="primary" icon={Landmark} loading={save.loading} loadingText="Saving…" onClick={doSave}>Save Bank Details</Btn>
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
